import React, { useEffect, useState } from 'react';
import Icon from './shared/Icon';
import { procurementService, infrastructureService } from '../services/api';
import { secaoCaiDeProposito } from '../design';

/**
 * Custo da API do Claude para a aba Relatórios.
 *
 * Por que aqui e não só na Automação: o número já existia, mas morava numa tela
 * de INFRAESTRUTURA. Quem abre Relatórios é quem decide orçamento, e não vai
 * procurar gasto de API dentro de um painel de sessão e proxy.
 *
 * São DUAS fontes, e elas somam:
 *   · banco (`claude_api_usage`) — análise de edital e geração de proposta;
 *   · playwright (`claude-usage.json`) — resolução de captcha.
 *
 * ⚠️ Regra que este card segue, e que é a lição do dia 13/09/2026: quando uma
 * fonte não responde, ele DIZ isso em vez de exibir um total menor com cara de
 * completo. Meia soma silenciosa é pior que erro visível — foi exatamente assim
 * que o card de custo ficou meses mostrando 3x o valor real sem ninguém notar.
 */

interface Agregado {
  calls: number;
  costUsd: number;
}

interface Props {
  /** Câmbio opcional. Sem ele o card fica em USD — é como o dado é gravado. */
  usdBrl?: number | null;
}

const FEATURE_LABELS: Record<string, string> = {
  edital_analysis: 'Análise de edital',
  proposal_generation: 'Geração de proposta',
  captcha: 'Captcha (legado)',
  'captcha-champion': 'Captcha (champion)',
  'captcha-challenger': 'Captcha (challenger)',
};

const mesLabel = (ym: string) => {
  const [a, m] = ym.split('-');
  const d = new Date(Number(a), Number(m) - 1, 1);
  return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit', timeZone: 'America/Sao_Paulo' });
};

const usd = (v: number) => `US$ ${v.toFixed(2)}`;

export default function ClaudeCostReportCard({ usdBrl }: Props) {
  const [loading, setLoading] = useState(true);
  const [porMes, setPorMes] = useState<Record<string, Agregado>>({});
  const [porFeature, setPorFeature] = useState<Record<string, Agregado>>({});
  const [modelos, setModelos] = useState<string[]>([]);
  const [primeiroDia, setPrimeiroDia] = useState<string | null>(null);
  /** Fontes que NÃO responderam — o card mostra, não engole. */
  const [falhas, setFalhas] = useState<string[]>([]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const [banco, captcha] = await Promise.all([
        procurementService.aiAnalyzerUsage().catch(secaoCaiDeProposito('o card lista a fonte indisponível em "indisponiveis"')),
        infrastructureService.getClaudeUsage().catch(secaoCaiDeProposito('o card lista a fonte indisponível em "indisponiveis"')),
      ]);
      if (!vivo) return;

      const indisponiveis: string[] = [];
      if (!banco) indisponiveis.push('editais e propostas (backend)');
      if (!captcha) indisponiveis.push('captcha (serviço de automação)');

      const meses: Record<string, Agregado> = {};
      const features: Record<string, Agregado> = {};
      let menorDia: string | null = null;

      const somaDias = (byDay?: Record<string, { calls: number; costUsd: number }>) => {
        for (const [dia, v] of Object.entries(byDay || {})) {
          const ym = dia.slice(0, 7);
          if (!meses[ym]) meses[ym] = { calls: 0, costUsd: 0 };
          meses[ym].calls += v.calls || 0;
          meses[ym].costUsd += v.costUsd || 0;
          if (!menorDia || dia < menorDia) menorDia = dia;
        }
      };
      const somaFeatures = (byFeature?: Record<string, { calls: number; costUsd: number }>) => {
        for (const [f, v] of Object.entries(byFeature || {})) {
          if (!features[f]) features[f] = { calls: 0, costUsd: 0 };
          features[f].calls += v.calls || 0;
          features[f].costUsd += v.costUsd || 0;
        }
      };

      somaDias(banco?.byDay as any);
      somaDias(captcha?.byDay as any);
      somaFeatures(banco?.byFeature as any);
      somaFeatures(captcha?.byFeature as any);

      // Modelos LIDOS do dado, nunca escritos à mão — rótulo fixo de modelo
      // envelhece sem avisar (o card da Automação dizia "Sonnet 4.5" enquanto o
      // serviço já rodava claude-sonnet-5).
      const mods = Array.from(
        new Set((banco?.last100 || []).map((u: any) => u.model).filter(Boolean)),
      ) as string[];

      setPorMes(meses);
      setPorFeature(features);
      setModelos(mods);
      setPrimeiroDia(menorDia);
      setFalhas(indisponiveis);
      setLoading(false);
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const chaves = Object.keys(porMes).sort().reverse();
  const ultimos6 = chaves.slice(0, 6);
  const hoje = new Date();
  const ymAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const mesAtual = porMes[ymAtual] || { calls: 0, costUsd: 0 };
  const acumulado = Object.values(porMes).reduce(
    (a, v) => ({ calls: a.calls + v.calls, costUsd: a.costUsd + v.costUsd }),
    { calls: 0, costUsd: 0 },
  );
  const maiorMes = Math.max(...ultimos6.map((k) => porMes[k].costUsd), 0.0001);
  const featuresOrdenadas = Object.entries(porFeature).sort((a, b) => b[1].costUsd - a[1].costUsd);

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      <div className="p-4 border-b border-slate-800 flex items-center gap-3">
        <div className="p-2 bg-violet-500/10 rounded-lg">
          <Icon name="chart" className="w-5 h-5 text-violet-400" />
        </div>
        <div className="min-w-0">
          <h3 className="text-white font-semibold">Custo Claude API</h3>
          <p className="text-slate-400 text-sm">
            Análise de edital, geração de proposta e resolução de captcha
          </p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {loading ? (
          <p className="text-slate-400 text-sm">Carregando…</p>
        ) : (
          <>
            {falhas.length > 0 && (
              <div className="text-[12px] leading-snug text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
                <strong>Soma incompleta.</strong> Não consegui ler: {falhas.join(' e ')}. Os
                valores abaixo cobrem só o que respondeu — não são o total.
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-3xl font-bold text-white">{usd(mesAtual.costUsd)}</p>
                <p className="text-sm text-slate-400 mt-1">
                  Mês atual · {mesAtual.calls} chamada(s)
                </p>
                {usdBrl ? (
                  <p className="text-xs text-slate-500 mt-0.5">
                    ≈ R$ {(mesAtual.costUsd * usdBrl).toFixed(2)}
                  </p>
                ) : null}
              </div>
              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-3xl font-bold text-white">{usd(acumulado.costUsd)}</p>
                <p className="text-sm text-slate-400 mt-1">
                  Acumulado · {acumulado.calls} chamada(s)
                </p>
                {/* A janela vai junto do número: "acumulado" sem data é convite
                    pra ler como "por mês". */}
                <p className="text-xs text-slate-500 mt-0.5">
                  {primeiroDia ? `desde ${primeiroDia.split('-').reverse().join('/')}` : 'sem registro'}
                </p>
              </div>
              <div className="bg-slate-800 rounded-lg p-4 col-span-2 md:col-span-1">
                <p className="text-sm text-slate-400">Modelo(s) em uso</p>
                <p className="text-slate-200 text-sm font-mono mt-1 break-words">
                  {modelos.length ? modelos.join(', ') : '—'}
                </p>
                <p className="text-xs text-slate-500 mt-1">lido dos registros, não fixo no código</p>
              </div>
            </div>

            {featuresOrdenadas.length > 0 && (
              <div>
                <p className="text-slate-300 text-sm font-medium mb-2">Por tipo de uso</p>
                <div className="space-y-1.5">
                  {featuresOrdenadas.map(([f, v]) => (
                    <div key={f} className="flex justify-between items-baseline gap-4 text-sm">
                      <span className="text-slate-400 min-w-0 break-words">
                        {FEATURE_LABELS[f] || f}
                      </span>
                      <span className="text-slate-200 shrink-0">
                        {usd(v.costUsd)} <span className="text-slate-500">({v.calls})</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ultimos6.length > 0 && (
              <div>
                <p className="text-slate-300 text-sm font-medium mb-2">Últimos meses</p>
                <div className="space-y-1.5">
                  {ultimos6.map((ym) => (
                    <div key={ym} className="flex items-center gap-3 text-sm">
                      <span className="text-slate-400 w-16 shrink-0">{mesLabel(ym)}</span>
                      <div className="flex-1 bg-slate-900 rounded-full h-2 min-w-0">
                        <div
                          className="bg-violet-500 h-2 rounded-full"
                          style={{ width: `${Math.max(2, (porMes[ym].costUsd / maiorMes) * 100)}%` }}
                        />
                      </div>
                      <span className="text-slate-200 w-20 text-right shrink-0">
                        {usd(porMes[ym].costUsd)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!ultimos6.length && !falhas.length && (
              <p className="text-slate-400 text-sm">Nenhuma chamada registrada ainda.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
